import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../lib/convex';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, ExternalLink } from 'lucide-react';
import { Footerdemo } from '../ui/footer-section';
import { SEOHead } from '@/lib/seo';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const post = useQuery(api.blog.getPublishedBlogPostBySlug, { slug: slug || '' });

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col pt-16">
        <div className="flex-1 max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Post not found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
        <Footerdemo />
      </div>
    );
  }

  // Safe content - sanitize all user-generated content
  const safeTitle = DOMPurify.sanitize(post.title);
  const safeContent = DOMPurify.sanitize(post.content);
  const safeImageUrl = post.imageUrl ? DOMPurify.sanitize(post.imageUrl) : undefined;
  const safeAuthorName = post.authorBio ? DOMPurify.sanitize(post.authorBio.name) : '';
  const safeAuthorHeadline = post.authorBio ? DOMPurify.sanitize(post.authorBio.headline) : '';
  const safeAuthorDescription = post.authorBio ? DOMPurify.sanitize(post.authorBio.description) : '';
  const safeAuthorImageUrl = post.authorBio && post.authorBio.imageUrl ? DOMPurify.sanitize(post.authorBio.imageUrl) : '';

  return (
    <div className="min-h-screen flex flex-col pt-16">
      <SEOHead
        title={post.title}
        description={post.summary}
        image={post.imageUrl}
        type="article"
        publishedTime={post.publishedAt ? new Date(post.publishedAt).toISOString() : undefined}
        modifiedTime={new Date(post.updatedAt).toISOString()}
        author={post.authorBio ? {
          name: post.authorBio.name,
          url: post.authorBio.link1?.url
        } : undefined}
        tags={post.tags}
        url={typeof window !== 'undefined' ? window.location.href : undefined}
        canonicalUrl={`https://aiseobloggenerator.com/blog/${post.slug}`}
      />
      <article className="flex-1">
        {safeImageUrl && (
          <div className="w-full max-w-[800px] mx-auto mb-8">
            <img
              src={safeImageUrl}
              alt={safeTitle}
              className="w-full h-auto rounded-lg"
              style={{
                objectFit: post.imageStyle || 'cover',
                height: post.imageHeight || 'auto'
              }}
            />
          </div>
        )}
        
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-8">
            <Link to="/blog">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>

          <h1 className="text-4xl font-bold mb-4">{safeTitle}</h1>
          
          <div className="flex items-center gap-4 text-gray-600 mb-8">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <time dateTime={new Date(post.publishedAt || post.createdAt).toISOString()}>
                {formatDistanceToNow(post.publishedAt || post.createdAt, { addSuffix: true })}
              </time>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              components={{
                // Use DOMPurify in each component that renders user content
                img: ({src, alt, ...props}) => {
                  // Only render trusted image sources
                  const safeSrc = src ? DOMPurify.sanitize(src) : '';
                  const safeAlt = alt ? DOMPurify.sanitize(alt) : '';
                  
                  // Only allow images from certain trusted domains
                  const trustedDomains = [
                    'images.unsplash.com',
                    'modest-heron-656.convex.cloud',
                    window.location.hostname,
                  ];
                  
                  const isUrlTrusted = () => {
                    try {
                      const url = new URL(safeSrc);
                      return trustedDomains.some(domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`));
                    } catch {
                      return false;
                    }
                  };
                  
                  if (!safeSrc || !isUrlTrusted()) {
                    return <span className="text-red-500">[Image removed: untrusted source]</span>;
                  }
                  
                  return <img src={safeSrc} alt={safeAlt} {...props} className="rounded-lg shadow-md my-8" />;
                },
                
                a: ({href, children, ...props}) => {
                  // Sanitize href
                  const safeHref = href ? DOMPurify.sanitize(href) : '#';
                  
                  // Add target="_blank" and rel attributes for external links
                  const isExternal = safeHref.startsWith('http') && !safeHref.includes(window.location.hostname);
                  const rel = isExternal ? "noopener noreferrer nofollow" : undefined;
                  const target = isExternal ? "_blank" : undefined;
                  
                  return (
                    <a 
                      href={safeHref} 
                      target={target}
                      rel={rel}
                      {...props} 
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {children}
                    </a>
                  );
                }
              }}
            >
              {safeContent}
            </ReactMarkdown>
          </div>

          {post.authorBio && (
            <div className="mt-16 pt-8 border-t">
              <div className="flex items-center gap-6">
                {safeAuthorImageUrl && (
                  <img
                    src={safeAuthorImageUrl}
                    alt={safeAuthorName}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="text-xl font-bold">{safeAuthorName}</h3>
                  <p className="text-gray-600">{safeAuthorHeadline}</p>
                </div>
              </div>
              <p className="mt-4 text-gray-600">{safeAuthorDescription}</p>
              <div className="mt-4 flex gap-4">
                {post.authorBio.link1 && (
                  <a
                    href={DOMPurify.sanitize(post.authorBio.link1.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {DOMPurify.sanitize(post.authorBio.link1.text)}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {post.authorBio.link2 && (
                  <a
                    href={DOMPurify.sanitize(post.authorBio.link2.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {DOMPurify.sanitize(post.authorBio.link2.text)}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </article>
      <Footerdemo />
    </div>
  );
}